import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Input from '@amzn/awsui-components-core/input';
import Modal from '@amzn/awsui-components-core/modal';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import React, { useEffect, useMemo, useState } from 'react';
import { getHierarchyTree } from 'src/components/star-grid/services/starGridService';
import { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';
import { EMPTY_MEMBER_SUBSET, useMemberSubsetDraft } from 'src/components/star-grid/hooks/useMemberSubsetDraft';
import PnlMemberSubsetGrid from 'src/components/star-grid/components/PnlMemberSubsetGrid';
import {
  allPositionKeys,
  applyExclusions,
  searchMemberRows,
  indexMemberTree,
  MemberSubsetOp,
  scopeRows,
  seedExpansion,
  visibleMemberRows
} from 'src/components/star-grid/utils/memberSubset';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import styles from './styles/PnlMemberSubsetModal.module.scss';

export interface PnlMemberSubsetModalProps {
  readonly hierarchyId: string;
  /** Dimension label for the heading, until the fetched tree supplies its own. */
  readonly dimensionLabel: string;
  readonly visible: boolean;
  readonly onDismiss: () => void;
  /**
   * Fires on OK with the checked members in hierarchy order, plus the hierarchy's total member count
   * so the caller can label the chip `x of y`. Empty members = unrestricted.
   */
  readonly onConfirm?: (members: string[], totalMembers: number, checkedKeys: string[]) => void;
  /**
   * Position keys to restore on open — what OK last handed back. Member ids alone would not do: a
   * multi-parent member has one id but several positions, so ids could not say which was ticked.
   */
  readonly initialChecked?: readonly string[];
  readonly testId?: string;
}

/**
 * The member subset editor (TM1 subset-editor shape): one pane that IS the subset, opened from the
 * chip's ☰ button.
 *
 * The WHOLE hierarchy arrives in one uncapped fetch (GET /pnl-hierarchies/{id}/tree) because the
 * Keep/Leaves operations are set algebra over the tree — a partial tree would answer them wrongly
 * rather than slowly. These hierarchies are thousands of members, so that is affordable, and it
 * keeps every operation local and instant.
 *
 * Rows are addressed by tree POSITION, not member id: a multi-parent member appears under each of
 * its parents, and checking it in one place must not check it in another.
 *
 * Everything starts collapsed, so only roots render until the user expands — which is why this
 * needs no virtualization yet. Revisit if a future bulk-expand lands.
 */
const PnlMemberSubsetModal: React.FC<PnlMemberSubsetModalProps> = ({
  hierarchyId,
  dimensionLabel,
  visible,
  onDismiss,
  onConfirm,
  initialChecked,
  testId
}) => {
  const [members, setMembers] = useState<PnlHierarchyMember[]>([]);
  const [memberCount, setMemberCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reopening resumes where OK left off, rather than starting blank.
  const restored = useMemo(() => new Set(initialChecked ?? []), [initialChecked]);
  const { draft, setExpanded, setChecked, applyOp, remove, reset } = useMemberSubsetDraft({ ...EMPTY_MEMBER_SUBSET, checked: restored });
  // Same grid theme the report grid uses, so the editor does not look like a different product.
  const { agGridTheme } = useTheme();
  const [search, setSearch] = useState('');

  /*
   * Fetch the tree whenever the editor is open for a hierarchy.
   *
   * Guarded by a LOCAL cancellation flag, not by a ref remembering "already asked". StrictMode runs
   * an effect, cleans it up, then runs it again — so a persistent latch makes the first run start a
   * request that its own cleanup aborts, and the second run skip fetching altogether, leaving the
   * modal loading forever. A per-run flag lets the second run simply refetch, which is cheap: the
   * server caches the tree per hierarchy.
   */
  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getHierarchyTree(hierarchyId, controller.signal)
      .then((tree) => {
        if (cancelled) return;
        setMembers(tree.members);
        setMemberCount(tree.member_count);
        setLoading(false);
      })
      .catch((cause: Error) => {
        if (cancelled) return;
        setError(cause.message || 'Could not load the hierarchy.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, hierarchyId]);

  const index = useMemo(() => indexMemberTree(members), [members]);
  // Two modes: browsing the whole hierarchy until an operation runs, then the built scope, whose
  // rows are levelled FROM THE KEPT NODE so the displayed depth auto-updates.
  const isSearching = search.trim().length > 0;

  // The rows the GRID gets: the operation's whole row set, expanded or not. The grid owns
  // expand/collapse and virtualises, so handing it everything costs nothing and keeps every
  // expander working -- supplying only visible rows made a collapsed node unopenable.
  const gridRows = useMemo(() => {
    const everything = allPositionKeys(index);
    const rows = draft.scope ? scopeRows(index, draft.scope, everything) : visibleMemberRows(members, everything);
    // Removals are applied last, so they survive whichever way the rows were produced.
    return applyExclusions(rows, draft.excluded);
  }, [draft.scope, index, members, draft.excluded]);

  // Searching shows ONLY the matches, each with the rollup above it and its own subtree below, so a
  // hit can still be expanded into.
  const { rows: displayRows, expand } = useMemo(() => searchMemberRows(gridRows, search), [gridRows, search]);

  const initiallyExpanded = useMemo(() => {
    // Open the hits' ancestors so the hits are on screen; the hits stay closed so a high-level match
    // does not dump its whole subtree. The grid applies this per node as it first renders, so the
    // remount below is what makes it re-apply on each keystroke.
    if (isSearching) return expand;
    return draft.scope ? seedExpansion(index, draft.scope) : new Set<string>();
  }, [isSearching, expand, index, draft.scope]);

  /*
   * Membership is ALWAYS what is ticked — never what an operation happens to be showing.
   *
   * Deriving it from the visible rows meant running Keep silently selected everything it revealed,
   * so the count beside OK jumped without the user choosing anything. The operations shape the view;
   * the checkboxes choose the members.
   */
  const memberKeys = useMemo(() => [...draft.checked], [draft.checked]);
  const isUnrestricted = memberKeys.length === 0;

  const handleConfirm = (): void => {
    // Position keys collapse to member ids here: the same member kept at two positions is still one
    // member to the report. Hierarchy order is preserved.
    const ordered = memberKeys
      .map((key) => index.get(key))
      .filter((entry) => entry !== undefined)
      .sort((left, right) => (left?.order ?? 0) - (right?.order ?? 0))
      .map((entry) => entry?.id as string);
    onConfirm?.([...new Set(ordered)], memberCount, memberKeys);
    onDismiss();
  };

  const runOp = (op: MemberSubsetOp, selected: readonly string[]): void => applyOp(op, selected, index);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={`Select members — ${dimensionLabel}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Box color="text-body-secondary" fontSize="body-s" padding={{ top: 'xxs' }}>
              {isUnrestricted ? 'All members' : `${memberKeys.length} selected`}
              {memberCount > 0 ? ` · ${memberCount} members` : ''}
            </Box>
            <Button variant="link" onClick={onDismiss} data-testid={testId ? `${testId}-cancel` : undefined}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-testid={testId ? `${testId}-ok` : undefined}>
              {isUnrestricted ? 'OK' : `OK (${memberKeys.length})`}
            </Button>
          </SpaceBetween>
        </Box>
      }
      data-testid={testId}
    >
      {error && (
        <Alert type="error" header="Could not load the hierarchy">
          {error}
        </Alert>
      )}

      {!error && (
        <div className={styles.searchBox}>
          <Input
            type="search"
            value={search}
            onChange={({ detail }) => setSearch(detail.value)}
            placeholder="Find members"
            ariaLabel="Find members"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
      )}

      {!error && (
        <div className={styles.memberList} data-testid={testId ? `${testId}-list` : undefined}>
          {loading && displayRows.length === 0 ? (
            <div className={styles.emptyState}>
              <Spinner /> Loading members…
            </div>
          ) : displayRows.length === 0 ? (
            <div className={styles.emptyState}>{isSearching ? 'No members match your search.' : 'This hierarchy has no members.'}</div>
          ) : (
            <PnlMemberSubsetGrid
              // Remounting on a new operation re-seeds the grid's own expansion; pushing expansion
              // into a live grid instead is how feedback loops with onRowGroupOpened start.
              key={`${draft.scope ? `${draft.scope.op}:${draft.scope.anchors.join('|')}` : 'all'}:${search.trim().toLowerCase()}`}
              rows={displayRows}
              theme={agGridTheme}
              checked={draft.checked}
              onCheckedChanged={setChecked}
              onExpandedChanged={setExpanded}
              initiallyExpanded={initiallyExpanded}
              initiallyChecked={restored}
              onRunOp={runOp}
              onRemove={(selected, withDescendants) => remove(selected, withDescendants, index)}
              testId={testId ? `${testId}-grid` : undefined}
            />
          )}
        </div>
      )}

      {displayRows.length > 0 && (
        <Box color="text-body-secondary" fontSize="body-s" padding={{ top: 'xs' }}>
          {/* Keep is destructive, so getting the whole hierarchy back must always be one click.
              Deliberately not labelled "All members" -- that is what the footer STATUS says, and two
              controls reading the same thing made the state unreadable. */}
          <Button
            variant="link"
            onClick={reset}
            // Disabled only when there is genuinely nothing to undo. Keyed off the SCOPE too: an
            // operation leaves nothing ticked, so testing the selection alone made this dead exactly
            // when it was most needed.
            disabled={draft.scope === null && draft.checked.size === 0 && draft.expanded.size === 0 && draft.excluded.size === 0}
            data-testid={testId ? `${testId}-show-all` : undefined}
          >
            Show all
          </Button>
        </Box>
      )}
    </Modal>
  );
};

export default PnlMemberSubsetModal;
